import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Question, QuestionOption, Topic } from '../types';
import {
  Clock,
  AlertCircle,
  CheckCircle,
  XCircle,
  ArrowRight,
  ArrowLeft,
} from 'lucide-react';

export default function TestPage() {
  const { topicId } = useParams<{ topicId: string }>();
  const navigate = useNavigate();
  const [topic, setTopic] = useState<Topic | null>(null);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState<Map<number, any>>(new Map());
  const [attemptId, setAttemptId] = useState<number | null>(null);
  const [timeRemaining, setTimeRemaining] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    startTest();
  }, [topicId]);

  useEffect(() => {
    if (timeRemaining === null) return;

    if (timeRemaining <= 0) {
      handleSubmit();
      return;
    }

    const timer = setInterval(() => {
      setTimeRemaining((prev) => (prev !== null ? prev - 1 : null));
    }, 1000);

    return () => clearInterval(timer);
  }, [timeRemaining]);

  const startTest = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/test/start`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ topicId: parseInt(topicId!) }),
      });

      if (!response.ok) throw new Error('Failed to start test');

      const data = await response.json();
      setTopic(data.topic);
      setQuestions(data.questions);
      setAttemptId(data.attemptId);
      if (data.timeLimit) {
        setTimeRemaining(data.timeLimit);
      }
    } catch (error) {
      console.error('Failed to start test:', error);
      alert('Fehler beim Starten des Tests');
      navigate('/topics');
    } finally {
      setLoading(false);
    }
  };

  const handleAnswer = (questionId: number, answer: any) => {
    setAnswers(new Map(answers.set(questionId, answer)));
  };

  const handleNext = () => {
    if (currentIndex < questions.length - 1) {
      setCurrentIndex(currentIndex + 1);
    }
  };

  const handlePrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  const handleSubmit = async () => {
    if (submitting) return;
    setSubmitting(true);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/test/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          attemptId,
          answers: Array.from(answers.entries()).map(([questionId, answer]) => ({
            questionId,
            answer,
          })),
        }),
      });

      if (!response.ok) throw new Error('Failed to submit test');

      const data = await response.json();
      navigate(`/results?attemptId=${data.attemptId}`);
    } catch (error) {
      console.error('Failed to submit test:', error);
      alert('Fehler beim Absenden des Tests');
    } finally {
      setSubmitting(false);
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const isAnswered = (questionId: number) => answers.has(questionId);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  const currentQuestion = questions[currentIndex];
  const progress = ((currentIndex + 1) / questions.length) * 100;

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{topic?.title}</h1>
            <p className="mt-1 text-sm text-gray-500">
              Frage {currentIndex + 1} von {questions.length}
            </p>
          </div>
          {timeRemaining !== null && (
            <div
              className={`flex items-center space-x-2 px-4 py-2 rounded-lg ${
                timeRemaining < 300 ? 'bg-red-100' : 'bg-gray-100'
              }`}
            >
              <Clock
                className={`h-5 w-5 ${
                  timeRemaining < 300 ? 'text-red-600' : 'text-gray-600'
                }`}
              />
              <span
                className={`font-mono text-lg font-semibold ${
                  timeRemaining < 300 ? 'text-red-600' : 'text-gray-900'
                }`}
              >
                {formatTime(timeRemaining)}
              </span>
            </div>
          )}
        </div>

        {/* Progress Bar */}
        <div className="mt-4 w-full bg-gray-200 rounded-full h-2">
          <div
            className="bg-primary-600 h-2 rounded-full transition-all"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      {/* Question */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="mb-6">
          <div className="flex items-start justify-between">
            <h2 className="text-lg font-semibold text-gray-900">
              {currentQuestion.question_text}
            </h2>
            {isAnswered(currentQuestion.id) && (
              <CheckCircle className="h-5 w-5 text-green-500 ml-2" />
            )}
          </div>
          {currentQuestion.question_context && (
            <p className="mt-2 text-sm text-gray-600 bg-gray-50 p-3 rounded-md">
              {currentQuestion.question_context}
            </p>
          )}
        </div>

        {/* Answer Options */}
        <div className="space-y-3">
          {currentQuestion.question_type === 'single_choice' &&
            currentQuestion.options?.map((option) => (
              <button
                key={option.id}
                onClick={() => handleAnswer(currentQuestion.id, option.id)}
                className={`w-full text-left p-4 rounded-lg border-2 transition-colors ${
                  answers.get(currentQuestion.id) === option.id
                    ? 'border-primary-500 bg-primary-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center">
                  <div
                    className={`h-5 w-5 rounded-full border-2 mr-3 flex items-center justify-center ${
                      answers.get(currentQuestion.id) === option.id
                        ? 'border-primary-500 bg-primary-500'
                        : 'border-gray-300'
                    }`}
                  >
                    {answers.get(currentQuestion.id) === option.id && (
                      <div className="h-2 w-2 bg-white rounded-full" />
                    )}
                  </div>
                  <span className="text-gray-900">{option.option_text}</span>
                </div>
              </button>
            ))}

          {currentQuestion.question_type === 'multiple_choice' &&
            currentQuestion.options?.map((option) => {
              const selectedOptions =
                (answers.get(currentQuestion.id) as number[]) || [];
              const isSelected = selectedOptions.includes(option.id);

              return (
                <button
                  key={option.id}
                  onClick={() => {
                    const current = selectedOptions;
                    const updated = isSelected
                      ? current.filter((id) => id !== option.id)
                      : [...current, option.id];
                    handleAnswer(currentQuestion.id, updated);
                  }}
                  className={`w-full text-left p-4 rounded-lg border-2 transition-colors ${
                    isSelected
                      ? 'border-primary-500 bg-primary-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center">
                    <div
                      className={`h-5 w-5 rounded border-2 mr-3 flex items-center justify-center ${
                        isSelected
                          ? 'border-primary-500 bg-primary-500'
                          : 'border-gray-300'
                      }`}
                    >
                      {isSelected && (
                        <CheckCircle className="h-3 w-3 text-white" />
                      )}
                    </div>
                    <span className="text-gray-900">{option.option_text}</span>
                  </div>
                </button>
              );
            })}

          {currentQuestion.question_type === 'true_false' &&
            currentQuestion.options?.map((option) => (
              <button
                key={option.id}
                onClick={() => handleAnswer(currentQuestion.id, option.id)}
                className={`w-full text-left p-4 rounded-lg border-2 transition-colors ${
                  answers.get(currentQuestion.id) === option.id
                    ? 'border-primary-500 bg-primary-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center">
                  <div
                    className={`h-5 w-5 rounded-full border-2 mr-3 flex items-center justify-center ${
                      answers.get(currentQuestion.id) === option.id
                        ? 'border-primary-500 bg-primary-500'
                        : 'border-gray-300'
                    }`}
                  >
                    {answers.get(currentQuestion.id) === option.id && (
                      <div className="h-2 w-2 bg-white rounded-full" />
                    )}
                  </div>
                  <span className="text-gray-900 font-medium">
                    {option.option_text}
                  </span>
                </div>
              </button>
            ))}

          {(currentQuestion.question_type === 'fill_blank' ||
            currentQuestion.question_type === 'calculation' ||
            currentQuestion.question_type === 'practical_scenario') && (
            <textarea
              value={answers.get(currentQuestion.id) || ''}
              onChange={(e) =>
                handleAnswer(currentQuestion.id, e.target.value)
              }
              className="w-full p-4 border-2 border-gray-200 rounded-lg focus:border-primary-500 focus:ring-1 focus:ring-primary-500 resize-none"
              rows={
                currentQuestion.question_type === 'practical_scenario' ? 10 : 3
              }
              placeholder="Deine Antwort eingeben..."
            />
          )}
        </div>
      </div>

      {/* Navigation */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between">
          <button
            onClick={handlePrevious}
            disabled={currentIndex === 0}
            className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Zurück
          </button>

          <div className="text-sm text-gray-600">
            {answers.size} von {questions.length} beantwortet
          </div>

          {currentIndex === questions.length - 1 ? (
            <button
              onClick={handleSubmit}
              disabled={submitting}
              className="inline-flex items-center px-6 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 disabled:opacity-50"
            >
              {submitting ? 'Wird abgesendet...' : 'Test abschließen'}
              <CheckCircle className="h-4 w-4 ml-2" />
            </button>
          ) : (
            <button
              onClick={handleNext}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700"
            >
              Weiter
              <ArrowRight className="h-4 w-4 ml-2" />
            </button>
          )}
        </div>
      </div>

      {/* Question Overview */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-sm font-medium text-gray-700 mb-3">
          Fragenübersicht
        </h3>
        <div className="grid grid-cols-10 gap-2">
          {questions.map((q, idx) => (
            <button
              key={q.id}
              onClick={() => setCurrentIndex(idx)}
              className={`aspect-square rounded-md flex items-center justify-center text-sm font-medium transition-colors ${
                idx === currentIndex
                  ? 'bg-primary-600 text-white'
                  : isAnswered(q.id)
                  ? 'bg-green-100 text-green-700 hover:bg-green-200'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {idx + 1}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}